import CategoryRounded from '@mui/icons-material/CategoryRounded';
import {
  Box,
  Chip,
  MenuItem,
  Skeleton,
  TextField,
  Typography,
  type TextFieldProps,
} from '@mui/material';
import type { ReactNode } from 'react';
import { money } from '../../lib/format';
import { useShop } from '../../shop/ShopProvider';
import { categoryTree, EMPTY_SELECT_SLOTS, priceRange, type Category, type Variant } from './model';
import { useProductImageUrl } from './queries';

/** Square product picture: the first image, or a tinted tile with an icon while there is none. */
export function ProductThumb({
  productId,
  imageId,
  size = 44,
  radius = 12,
  icon,
}: {
  productId: string;
  imageId?: string;
  size?: number;
  radius?: number;
  icon?: ReactNode;
}) {
  const { url, loading } = useProductImageUrl(productId, imageId);
  if (imageId && loading)
    return (
      <Skeleton
        variant="rounded"
        width={size}
        height={size}
        sx={{ borderRadius: `${String(radius)}px`, flexShrink: 0 }}
      />
    );
  return (
    <Box
      sx={(t) => {
        const p = (t.vars ?? t).palette.primary.main;
        return {
          width: size,
          height: size,
          flexShrink: 0,
          borderRadius: `${String(radius)}px`,
          overflow: 'hidden',
          display: 'grid',
          placeItems: 'center',
          color: p,
          border: 1,
          borderColor: 'divider',
          background: url
            ? (t.vars ?? t).palette.background.paper
            : `linear-gradient(135deg, color-mix(in srgb, ${p} 14%, transparent), color-mix(in srgb, ${p} 4%, transparent))`,
          '& svg': { fontSize: Math.round(size * 0.45) },
        };
      }}
    >
      {url ? (
        <Box
          component="img"
          src={url}
          alt=""
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        (icon ?? <CategoryRounded />)
      )}
    </Box>
  );
}

/** "Rs 210 – Rs 245", or one price when all variants cost the same. */
export function PriceRangeText({
  variants,
}: {
  variants: Pick<Variant, 'salePrice' | 'isActive'>[];
}) {
  const { format } = useShop();
  const range = priceRange(variants);
  if (!range) return <>—</>;
  return (
    // Each amount stays on one line; a range may wrap between them on narrow screens.
    <Box component="span" className="tabular">
      <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
        {money(range.min, format)}
      </Box>
      {range.min !== range.max && (
        <>
          {' – '}
          <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
            {money(range.max, format)}
          </Box>
        </>
      )}
    </Box>
  );
}

export function ActiveChip({ active }: { active: boolean }) {
  return active ? (
    <Chip size="small" label="Active" color="success" variant="outlined" />
  ) : (
    <Chip size="small" label="Inactive" variant="outlined" />
  );
}

/** A select over the category tree, indented by depth. `''` means no category. */
export function CategorySelect({
  categories,
  exclude,
  noneLabel = 'No category',
  ...props
}: Omit<TextFieldProps, 'select' | 'children'> & {
  categories: Category[] | undefined;
  exclude?: Set<string>;
  noneLabel?: string;
}) {
  const tree = categoryTree(categories ?? []).filter((c) => !exclude?.has(c.id));
  return (
    <TextField select {...props} slotProps={{ ...props.slotProps, ...EMPTY_SELECT_SLOTS }}>
      <MenuItem value="">
        <Typography color="text.secondary">{noneLabel}</Typography>
      </MenuItem>
      {tree.map((c) => (
        <MenuItem key={c.id} value={c.id} sx={{ pl: 2 + c.depth * 2.25 }}>
          {c.depth > 0 && (
            <Box component="span" sx={{ color: 'text.disabled', mr: 0.75 }}>
              └
            </Box>
          )}
          {c.name}
        </MenuItem>
      ))}
    </TextField>
  );
}

/** Small uppercase heading for form sections, with an optional hint. */
export function SectionTitle({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="h6" sx={{ fontSize: '1.0625rem' }}>
          {title}
        </Typography>
        {hint && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {hint}
          </Typography>
        )}
      </Box>
      {action}
    </Box>
  );
}
